/**
 * Bilingual copy for the v2 landing. Kept in one place so the components
 * stay purely presentational. Keys that already exist in the global
 * translations (hero.description, services.items, impact.*) are read
 * through `t()` in the components instead of being duplicated here.
 */

export type Lang = "fr" | "en";

export interface TitlePart {
  text: string;
  italic?: boolean;
  accent?: boolean;
}

export const pickLang = (l: string): Lang => (l === "en" ? "en" : "fr");

export const V2 = {
  fr: {
    nav: {
      reel: "Lavages",
      services: "Services",
      process: "Méthode",
      pricing: "Tarifs",
      faq: "FAQ",
      b2b: "Entreprises",
      login: "Connexion",
      cta: "Laver ma paire",
      menu: "Menu",
      close: "Fermer",
    },
    hero: {
      eyebrow: "Soin sneakers · Île-de-France",
      // Split so the display font can italicise the key words.
      title: [
        { text: "Tes baskets ne sont pas " },
        { text: "vieilles,", italic: true },
        { text: " elles sont juste " },
        { text: "sales.", italic: true, accent: true },
      ] as TitlePart[],
      cta1: "Laver ma paire",
      cta2: "Devis photo gratuit",
      drag: "Glisser pour comparer",
      before: "Avant",
      after: "Après",
      stamp: "ATELIER · ÎLE-DE-FRANCE · SNEAKERS · ",
      stats: [
        ["24h", "Express"],
        ["5/5", "Avis Google"],
        ["500+", "Paires ravivées"],
      ],
      scroll: "Défiler",
    },
    brands: [
      "Nike",
      "Adidas",
      "Jordan",
      "New Balance",
      "Puma",
      "Converse",
      "Asics",
      "Balenciaga",
      "Reebok",
      "Vans",
    ],
    manifesto: {
      label: "Manifeste",
      text: "Une paire n'est jamais finie. Elle attend juste qu'on prenne soin d'elle.",
      principles: [
        [
          "01",
          "Produits professionnels",
          "Aucun produit ménager. Que du matériel d'atelier.",
        ],
        [
          "02",
          "Fait main, en Île-de-France",
          "Chaque paire est traitée individuellement, brosse en main.",
        ],
        [
          "03",
          "Notifié à chaque étape",
          "Photos, statut, retour : vous savez toujours où en est votre paire.",
        ],
      ],
    },
    reel: {
      label: "Nos lavages",
      title: "Le film.",
      subtitle:
        "Des résultats qui parlent d'eux-mêmes — avant et après en vidéo.",
      more: "Voir toutes les vidéos",
    },
    services: {
      label: "Services",
      title: "Ce qu'on fait.",
      subtitle: "Six façons de prendre soin de vos paires.",
      more: "Découvrir",
    },
    process: {
      label: "Méthode",
      title: "Quatre gestes, une paire neuve.",
      steps: [
        {
          n: "01",
          title: "Composez",
          desc: "Choisissez votre service de base et ajoutez vos options : collecte, express, imperméabilisation…",
          meta: "2 min",
        },
        {
          n: "02",
          title: "Déposez ou faites collecter",
          desc: "En point relais partenaire, ou collecte à domicile si vous avez choisi l'option.",
          meta: "Flexible",
        },
        {
          n: "03",
          title: "Nettoyage expert",
          desc: "Nos experts prennent soin de vos paires — vous êtes notifié à chaque étape.",
          meta: "1 – 7 jours",
        },
        {
          n: "04",
          title: "Récupérez",
          desc: "Vos sneakers reviennent comme neuves, prêtes à briller.",
          meta: "Inclus",
        },
      ],
      cta1: {
        title: "Composer ma prestation",
        sub: "Base + options, prix en direct",
      },
      cta2: {
        title: "Besoin spécifique ?",
        sub: "Envoyez des photos, recevez un devis",
      },
    },
    pricing: {
      label: "Tarifs",
      title: "Simple. Par paire.",
      subtitle:
        "Trois services de base, vos options en plus. Prix affiché avant confirmation.",
      perPair: "/ paire",
      popular: "Populaire",
      choose: "Choisir",
      duration: "Délai",
      pickup: "Collecte & livraison incluses",
      subs: "Voir les abonnements",
      custom: "Composer sur mesure",
    },
    impact: {
      label: "Impact",
      caption: "Une semelle synthétique met jusqu'à 1000 ans à se décomposer.",
    },
    b2b: {
      label: "Entreprises · B2B",
      title: "Vous êtes une entreprise ?",
      desc: "Nettoyage de sneakers en volume, collecte sur site en Île-de-France et facturation mensuelle. On s'occupe de tout.",
      segments: ["Conciergeries", "Hôtels", "Retail", "Clubs sportifs"],
      cta: "Découvrir l'offre entreprises",
    },
    faq: {
      label: "FAQ",
      title: "Questions, réponses.",
      more: "Toutes les questions",
      extra: [
        [
          "Quels types de services proposez-vous ?",
          "Nettoyage classique (intérieur et extérieur), Service Premium (nettoyage + retouches), Express 24h, petites retouches (coutures, œillets, petits décollements) sur devis, Protection Premium (imperméabilisation) et Finition Luxe (restauration haut de gamme).",
        ],
        [
          "Comment passer une commande ?",
          "1. Créez un compte gratuit, 2. Choisissez votre service, 3. Sélectionnez le nombre de paires, 4. Choisissez collecte à domicile ou point relais, 5. Confirmez et payez. C'est tout.",
        ],
        [
          "Comment suivre ma commande ?",
          "Connectez-vous à votre espace client, rubrique « Mes commandes » : statut en temps réel, notifications par email et SMS à chaque étape, photos avant/après selon le service.",
        ],
      ],
      items: [
        [
          "Quels types de chaussures acceptez-vous ?",
          "Toutes les sneakers et chaussures de ville : Nike, Adidas, Jordan, New Balance, Puma, Vans, Converse… ainsi que les paires de luxe (Balenciaga, Off-White, Yeezy). Pour les matières spéciales (daim, cuir exotique), optez pour le service Premium ou Luxe.",
        ],
        [
          "Quels sont les délais de traitement ?",
          "Express : 24h-48h · Premium : 24h-48h · Classic : 1-7 jours. Les délais peuvent varier selon l'affluence. Vous recevez des notifications à chaque étape.",
        ],
        [
          "Proposez-vous la collecte et la livraison ?",
          "Oui. Collecte et livraison à domicile (incluse dans la plupart des services) ou dépôt en point relais. Consultez la zone de service pour vérifier la disponibilité dans votre secteur.",
        ],
        [
          "Comment sont fixés les tarifs ?",
          "Les prix sont par paire et dépendent du service choisi. Le tarif inclut nettoyage, produits professionnels et retour. La collecte/livraison est incluse ou disponible en supplément (+15€). Un devis précis est affiché avant confirmation.",
        ],
        [
          "Comment préparer mes chaussures ?",
          "Enlevez les semelles amovibles et placez chaque paire dans un sac. Pas besoin de les nettoyer avant, c'est notre travail.",
        ],
        [
          "Et si je ne suis pas satisfait ?",
          "Contactez-nous dans les 48h suivant la réception : nous reprenons vos sneakers pour un nouveau traitement gratuit ou proposons un remboursement partiel.",
        ],
      ],
    },
    cta: {
      title: ["Prêt à raviver", "ta paire ?"],
      sub: "Composez votre prestation en 2 minutes. Devis photo gratuit pour les cas particuliers.",
      cta1: "Laver ma paire",
      cta2: "Demander un devis",
      email: "contact@lavetesbaskets.fr",
      hours: "Lun – Dim · 8h – 19h",
      city: "Île-de-France",
    },
    footer: {
      discover: "Découvrir",
      follow: "Suivre",
      legal: "Légal",
      links: [
        ["services", "Nos services"],
        ["sur-mesure", "Sur mesure"],
        ["tarifs", "Tarifs"],
        ["comment-ca-marche", "Comment ça marche"],
        ["devis", "Devis personnalisé"],
        ["entreprises", "Entreprises"],
        ["faq", "FAQ"],
      ],
      legalLinks: [
        ["privacy", "Confidentialité"],
        ["terms", "CGU"],
        ["legal", "Mentions légales"],
      ],
      rights: "Tous droits réservés.",
      v1: "Version classique",
    },
  },
  en: {
    nav: {
      reel: "Cleanings",
      services: "Services",
      process: "Method",
      pricing: "Pricing",
      faq: "FAQ",
      b2b: "Business",
      login: "Log in",
      cta: "Wash my pair",
      menu: "Menu",
      close: "Close",
    },
    hero: {
      eyebrow: "Sneaker care · Île-de-France",
      title: [
        { text: "Your sneakers aren't " },
        { text: "old,", italic: true },
        { text: " they're just " },
        { text: "dirty.", italic: true, accent: true },
      ] as TitlePart[],
      cta1: "Wash my pair",
      cta2: "Free photo estimate",
      drag: "Drag to compare",
      before: "Before",
      after: "After",
      stamp: "ATELIER · ÎLE-DE-FRANCE · SNEAKERS · ",
      stats: [
        ["24h", "Express"],
        ["5/5", "Google reviews"],
        ["500+", "Pairs revived"],
      ],
      scroll: "Scroll",
    },
    brands: [
      "Nike",
      "Adidas",
      "Jordan",
      "New Balance",
      "Puma",
      "Converse",
      "Asics",
      "Balenciaga",
      "Reebok",
      "Vans",
    ],
    manifesto: {
      label: "Manifesto",
      text: "A pair is never done. It's just waiting for someone to care for it.",
      principles: [
        [
          "01",
          "Professional products",
          "No household chemicals. Workshop-grade gear only.",
        ],
        [
          "02",
          "Handmade, in Île-de-France",
          "Every pair is treated individually, brush in hand.",
        ],
        [
          "03",
          "Notified at every step",
          "Photos, status, return: you always know where your pair is.",
        ],
      ],
    },
    reel: {
      label: "Our cleanings",
      title: "The film.",
      subtitle:
        "Results that speak for themselves — before and after on video.",
      more: "View all videos",
    },
    services: {
      label: "Services",
      title: "What we do.",
      subtitle: "Six ways to care for your pairs.",
      more: "Discover",
    },
    process: {
      label: "Method",
      title: "Four moves, one brand-new pair.",
      steps: [
        {
          n: "01",
          title: "Build it",
          desc: "Pick your base service and add your options: pickup, express, waterproofing…",
          meta: "2 min",
        },
        {
          n: "02",
          title: "Drop off or get picked up",
          desc: "At a partner relay point, or home pickup if you chose the option.",
          meta: "Flexible",
        },
        {
          n: "03",
          title: "Expert cleaning",
          desc: "Our experts take care of your pairs — you're notified at every step.",
          meta: "1 – 7 days",
        },
        {
          n: "04",
          title: "Get them back",
          desc: "Your sneakers come back like new, ready to shine.",
          meta: "Included",
        },
      ],
      cta1: { title: "Build my service", sub: "Base + add-ons, live pricing" },
      cta2: {
        title: "Something specific?",
        sub: "Send photos, get a tailored estimate",
      },
    },
    pricing: {
      label: "Pricing",
      title: "Simple. Per pair.",
      subtitle:
        "Three base services, your add-ons on top. Price shown before you confirm.",
      perPair: "/ pair",
      popular: "Popular",
      choose: "Choose",
      duration: "Turnaround",
      pickup: "Pickup & delivery included",
      subs: "See subscriptions",
      custom: "Build a custom service",
    },
    impact: {
      label: "Impact",
      caption: "A synthetic sole takes up to 1,000 years to decompose.",
    },
    b2b: {
      label: "Business · B2B",
      title: "Running a business?",
      desc: "Sneaker cleaning at volume, on-site pickup across Île-de-France and monthly billing. We handle everything.",
      segments: ["Concierge services", "Hotels", "Retail", "Sports clubs"],
      cta: "Explore the business offer",
    },
    faq: {
      label: "FAQ",
      title: "Questions, answers.",
      more: "All questions",
      extra: [
        [
          "What types of services do you offer?",
          "Classic cleaning (inside and outside), Premium service (cleaning + touch-ups), Express 24h, small touch-ups (stitching, eyelets, minor unsticking) on quote, Premium Protection (waterproofing) and Luxury Finish (high-end restoration).",
        ],
        [
          "How do I place an order?",
          "1. Create a free account, 2. Choose your service, 3. Select the number of pairs, 4. Choose home pickup or relay point, 5. Confirm and pay. That's it.",
        ],
        [
          "How do I track my order?",
          'Log in to your account, section "My orders": real-time status, email and SMS notifications at each step, before/after photos depending on the service.',
        ],
      ],
      items: [
        [
          "What types of shoes do you accept?",
          "All sneakers and city shoes: Nike, Adidas, Jordan, New Balance, Puma, Vans, Converse… plus luxury pairs (Balenciaga, Off-White, Yeezy). For special materials (suede, exotic leather), choose the Premium or Luxury service.",
        ],
        [
          "What are the turnaround times?",
          "Express: 24h-48h · Premium: 24h-48h · Classic: 3-7 days. Times may vary based on demand. You'll receive notifications at each step.",
        ],
        [
          "Do you offer pickup and delivery?",
          "Yes. Home pickup & delivery (included in most services) or relay point drop-off. Check the service area to verify availability in your area.",
        ],
        [
          "How are prices determined?",
          "Prices are per pair and depend on the chosen service. The rate includes cleaning, professional products and return. Pickup/delivery is included or available as an add-on (+€15). A precise quote is displayed before confirmation.",
        ],
        [
          "How should I prepare my shoes?",
          "Remove the insoles and put each pair in a bag. No need to clean them first, that's our job.",
        ],
        [
          "What if I'm not satisfied?",
          "Contact us within 48h of receipt: we'll take your sneakers back for a free second treatment or offer a partial refund.",
        ],
      ],
    },
    cta: {
      title: ["Ready to revive", "your pair?"],
      sub: "Build your service in 2 minutes. Free photo estimate for special cases.",
      cta1: "Wash my pair",
      cta2: "Request an estimate",
      email: "contact@lavetesbaskets.fr",
      hours: "Mon – Sun · 8am – 7pm",
      city: "Île-de-France",
    },
    footer: {
      discover: "Discover",
      follow: "Follow",
      legal: "Legal",
      links: [
        ["services", "Our services"],
        ["sur-mesure", "Custom care"],
        ["tarifs", "Pricing"],
        ["comment-ca-marche", "How it works"],
        ["devis", "Custom estimate"],
        ["entreprises", "Business"],
        ["faq", "FAQ"],
      ],
      legalLinks: [
        ["privacy", "Privacy"],
        ["terms", "Terms"],
        ["legal", "Legal notice"],
      ],
      rights: "All rights reserved.",
      v1: "Classic version",
    },
  },
};

export type V2Copy = (typeof V2)["fr"];
